import { useEffect, useState, Fragment } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { Card } from "reactstrap";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { useTranslation } from "react-i18next";
import PageHeader from "../../../components/PageHeader";
import Loading from "../../../components/utils/Loading";
import EmptyState from "../../../components/EmptyState";
import StandardTable from "../../../ui/components/StandardTable";
import WasteFlowHeader from "./WasteFlowHeader";
import WasteFlowRow from "./WasteFlowRow";
import Pagination from "../../../components/utils/Pagination";
import { fetchMaterialsCatalog } from "./wasteSortingItems";
import { getWasteFlowTemplates } from "./wasteFlowTemplates";

const SettingsWasteList = ({ token, API, basePath, hidePageHeader = true }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalEntry, setTotalEntry] = useState(0);
  const [totalEntryWithoutFilter, setTotalEntryWithoutFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [sortByField, setSortByField] = useState("label");
  const [orderSortByField, setOrderSortByField] = useState("1");
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const templates = getWasteFlowTemplates(t);

  const hasWasteData = tableData.length > 0;
  const hasAnyWasteData = totalEntryWithoutFilter > 0;
  const hasActiveFilters = Boolean(keywordFilter?.trim());
  const showEmptyState =
    !hasWasteData && !(hasAnyWasteData && hasActiveFilters);

  useEffect(() => {
    if (!token || !API) return;
    fetchMaterialsCatalog(API, token);
  }, [token, API]);

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API}wastecategory?page=${currentPage}&limit=${recordPerPage}`,
          {
            headers: { authorization: `Bearer ${token}` },
            params: {
              keywordFilter,
              listUnCodeFullFilter: {},
              listUnCodeEmptyFilter: {},
              listWasteCodeFullFilter: {},
              listWasteCodeEmptyFilter: {},
              sortByField,
              orderSortByField,
            },
          },
        );
        setTotalEntry(response.data.count ?? 0);
        setTableData(response.data.data || []);
        if (!hasActiveFilters) {
          setTotalEntryWithoutFilter(response.data.count ?? 0);
        }
      } catch (error) {
        console.error(error);
        setTableData([]);
        setTotalEntry(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    token,
    API,
    currentPage,
    recordPerPage,
    keywordFilter,
    sortByField,
    orderSortByField,
    hasActiveFilters,
  ]);

  useEffect(() => {
    if (!hasActiveFilters) {
      setTotalEntryWithoutFilter(totalEntry);
    }
  }, [totalEntry, hasActiveFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [keywordFilter, sortByField, orderSortByField]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordPerPage]);

  const handleOpenFlow = (waste) => {
    history.push(`${basePath}/${waste._id}`);
  };

  const handleCreate = () => {
    setShowTemplateModal(true);
  };

  const handleCreateTemplate = (templateId) => {
    history.push(`${basePath}/new?template=${templateId}`);
    setShowTemplateModal(false);
  };

  const handleCreateCustom = () => {
    history.push(`${basePath}/new`);
    setShowTemplateModal(false);
  };

  if (!token) return null;

  const listContent = (
    <div className="settings-content-wrapper settings-waste-list">
      {isLoading ? (
        <Loading color="#F5F5F5" />
      ) : (
        <div className="settings-list-shell">
          <Card className="table settings-waste-list__card">
            <StandardTable
              fluidColCount={4}
              fixedColWidth={0}
              containerClassName={
                showEmptyState
                  ? "table-standard-container--list-empty"
                  : undefined
              }
              columnAlignments={["left", "left", "left", "right"]}
              topBar={
                hasAnyWasteData ? (
                  <section className="table-standard-top-bar">
                    <div className="table-standard-top-bar-inner">
                      <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                        <div className="search-input-wrapper">
                          <i
                            className="fas fa-search search-input-icon"
                            aria-hidden
                          />
                          <input
                            type="text"
                            className="search-input"
                            value={keywordFilter}
                            placeholder={t("settingsWaste.searchPlaceholder")}
                            onChange={(e) => setKeywordFilter(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="create-button-wrapper">
                        <button
                          type="button"
                          className="create-button"
                          onClick={handleCreate}
                        >
                          {t("Create a new waste category")}
                        </button>
                      </div>
                    </div>
                  </section>
                ) : null
              }
              header={
                hasAnyWasteData ? (
                  <WasteFlowHeader
                    onSort={(field, order) => {
                      setSortByField(field);
                      setOrderSortByField(order);
                    }}
                    sortByField={sortByField}
                    orderSortByField={orderSortByField}
                  />
                ) : null
              }
              bottom={
                hasAnyWasteData ? (
                  <div className="table-standard-bottom table-pagination-bottom">
                    <Pagination
                      totalEntry={totalEntry}
                      recordPerPage={recordPerPage}
                      setRecordPerPage={setRecordPerPage}
                      currentPage={currentPage}
                      setCurrentPage={setCurrentPage}
                      resultPage={resultPage}
                      setResultPage={setResultPage}
                    />
                  </div>
                ) : null
              }
            >
              {hasWasteData ? (
                tableData.map((waste) => (
                  <Fragment key={waste._id}>
                    <WasteFlowRow data={waste} onOpen={handleOpenFlow} />
                  </Fragment>
                ))
              ) : hasAnyWasteData && hasActiveFilters ? (
                <div className="table-standard-row table-standard-row--empty">
                  <div
                    className="table-standard-row-cell"
                    style={{ gridColumn: "span 4" }}
                  >
                    {t("No results match your search")}
                  </div>
                </div>
              ) : (
                <EmptyState
                  title={t("emptyState.noFlux")}
                  description={t("emptyState.hintFlux")}
                  buttonText={t("Create a new waste category")}
                  onButtonClick={handleCreate}
                />
              )}
            </StandardTable>
          </Card>
        </div>
      )}
    </div>
  );

  const templateModal = (
    <Modal
      isOpen={showTemplateModal}
      toggle={() => setShowTemplateModal(false)}
      modalClassName="preset-modal"
      contentClassName="rounded-lg"
    >
      <ModalHeader toggle={() => setShowTemplateModal(false)}>
        <h4 className="font-semibold text-2xl m-0">
          {t("Create a new waste category")}
        </h4>
      </ModalHeader>
      <ModalBody style={{ padding: "24px" }}>
        <div style={{ marginBottom: "24px" }}>
          <h5 className="font-semibold text-lg mb-3">
            {t("Choose from a template")}
          </h5>
          <div className="flex flex-col gap-3">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleCreateTemplate(template.id)}
                className="flex items-center gap-3 px-4 h-12 rounded-md border border-[#e0e0e0] bg-white hover:bg-[#f5f5f5] transition-colors text-left w-full"
              >
                <span className="text-2xl leading-6" aria-hidden>
                  {template.icon}
                </span>
                <span className="text-sm font-medium text-[#222]">
                  {template.label}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-[#e0e0e0]">
          <h5 className="font-semibold text-lg mb-3">
            {t("Create a custom waste category")}
          </h5>
          <button
            type="button"
            onClick={handleCreateCustom}
            className="flex items-center gap-3 px-4 h-12 rounded-md border border-[#e0e0e0] bg-white hover:bg-[#f5f5f5] transition-colors text-left w-full"
          >
            <i
              className="fas fa-plus-circle text-2xl leading-6 text-[var(--akanthas-cyan)]"
              aria-hidden
            />
            <span className="text-sm font-medium text-[#222]">
              {t("Create a custom waste category")}
            </span>
          </button>
        </div>
      </ModalBody>
    </Modal>
  );

  if (hidePageHeader) {
    return (
      <>
        {listContent}
        {templateModal}
      </>
    );
  }

  return (
    <div className="settings-page-wrapper">
      <PageHeader
        title={t("Waste Flows")}
        showBackButton
        backRoute="/settings"
        backLabel={t("Back to Settings")}
      />
      {listContent}
      {templateModal}
    </div>
  );
};

export default SettingsWasteList;
